import { AssetTypeCode } from 'types/models/asset-type';
import { UnitType } from 'utils/get-unit-by-country';

const WEATHER_ASSET_TYPES = [
  AssetTypeCode.BAROMETRIC_PRESSURE,
  AssetTypeCode.DELTA_T,
  AssetTypeCode.DEW_POINT,
  AssetTypeCode.EVAPOTRANSPIRATION,
  AssetTypeCode.HUMIDITY,
  AssetTypeCode.RAIN_GAUGE,
  AssetTypeCode.SOLAR_RADIATION,
  AssetTypeCode.TEMPERATURE,
  AssetTypeCode.WEATHER_STATION_BATTERY,
  AssetTypeCode.WIND
] as const;

const WEATHER_ASSET_TYPES_SET: Set<AssetTypeCode> = new Set(WEATHER_ASSET_TYPES);

export const isWeatherAssetType = (assetTypeId: AssetTypeCode): boolean => {
  return WEATHER_ASSET_TYPES_SET.has(assetTypeId);
};

export type WeatherAssetType = typeof WEATHER_ASSET_TYPES[number];

export type WeatherUnitType =
  | UnitType.HUMIDITY
  | UnitType.PRESSURE_MD
  | UnitType.RAIN
  | UnitType.SOLAR_RADIATION
  | UnitType.TEMPERATURE
  | UnitType.VOLTAGE
  | UnitType.WIND_SPEED;

export const WEATHER_ASSET_TYPE_TO_LABEL: Record<WeatherAssetType, string> = {
  [AssetTypeCode.BAROMETRIC_PRESSURE]: 'Barometric Pressure',
  [AssetTypeCode.DELTA_T]: 'Delta-T',
  [AssetTypeCode.DEW_POINT]: 'Dew Point',
  [AssetTypeCode.EVAPOTRANSPIRATION]: 'Evapotranspiration',
  [AssetTypeCode.HUMIDITY]: 'Humidity',
  [AssetTypeCode.RAIN_GAUGE]: 'Rainfall',
  [AssetTypeCode.SOLAR_RADIATION]: 'Solar Radiation',
  [AssetTypeCode.TEMPERATURE]: 'Temperature',
  [AssetTypeCode.WEATHER_STATION_BATTERY]: 'Battery',
  [AssetTypeCode.WIND]: 'Wind'
};

// This mapping shares some similarity with getUnitTypeByAssetTypeId in src/store/modules/samples/saga/utils.ts
// but here it is narrower in scope in that it returns only WeatherUnitType.
export const WEATHER_ASSET_TYPE_TO_UNIT_TYPE: Record<WeatherAssetType, WeatherUnitType> = {
  [AssetTypeCode.BAROMETRIC_PRESSURE]: UnitType.PRESSURE_MD,
  [AssetTypeCode.DELTA_T]: UnitType.TEMPERATURE,
  [AssetTypeCode.DEW_POINT]: UnitType.TEMPERATURE,
  [AssetTypeCode.EVAPOTRANSPIRATION]: UnitType.RAIN,
  [AssetTypeCode.HUMIDITY]: UnitType.HUMIDITY,
  [AssetTypeCode.RAIN_GAUGE]: UnitType.RAIN,
  [AssetTypeCode.SOLAR_RADIATION]: UnitType.SOLAR_RADIATION,
  [AssetTypeCode.TEMPERATURE]: UnitType.TEMPERATURE,
  [AssetTypeCode.WEATHER_STATION_BATTERY]: UnitType.VOLTAGE,
  [AssetTypeCode.WIND]: UnitType.WIND_SPEED
};

/** Doc: https://farmbot-au.atlassian.net/wiki/spaces/DEV/pages/2267938817/Product+Spec+Weather+Stations#Weather-Sensor-Priority-List */
export const WEATHER_ASSET_TYPE_TO_PREFERRED_DISPLAY_ORDER: Record<WeatherAssetType, number> = {
  [AssetTypeCode.TEMPERATURE]: 1,
  [AssetTypeCode.DEW_POINT]: 2,
  [AssetTypeCode.RAIN_GAUGE]: 3,
  [AssetTypeCode.HUMIDITY]: 4,
  [AssetTypeCode.WIND]: 5,
  [AssetTypeCode.BAROMETRIC_PRESSURE]: 6,
  [AssetTypeCode.SOLAR_RADIATION]: 7,
  [AssetTypeCode.EVAPOTRANSPIRATION]: 8,
  [AssetTypeCode.DELTA_T]: 9,
  [AssetTypeCode.WEATHER_STATION_BATTERY]: 10
};